<template>
  <content-subpage
    class="embed-website-selection-content-subpage"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="false"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <!-- Left panel -->
    <template #main-content>
      <match-indicator v-if="matchWebsiteName" />
      <div class="info-header">
        <ellipsis-text class="website-name" :text="websiteName" />
      </div>
      <inline-loader v-if="isLoading" />
      <cv-form v-else class="website-details-left-panel">
        <!-- Icon -->
        <dl v-if="website.iconUrl" class="field-set icon">
          <dd class="field-value">
            <div class="image-placeholder">
              <img :src="getIconUrl()" class="thumbnail" />
            </div>
          </dd>
        </dl>
        <!-- Description -->
        <match-indicator v-if="matchDescription" />
        <dl class="field-set description">
          <dd class="field-value">{{ website.description }}</dd>
        </dl>
      </cv-form>
    </template>

    <!-- Right panel -->
    <template #sub-content>
      <inline-loader v-if="isLoading" />
      <cv-form v-else class="website-details-right-panel">
        <!-- Domains -->
        <dl class="field-set domains">
          <dd class="field-value">
            <cv-radio-group :vertical="false">
              <input-radio
                v-for="(domain, index) in domains"
                :id="domain.uuid"
                :key="index"
                :value="domain.uuid"
                :selected-value="selectedValue"
                class="domain"
                @change="handleChangeSelection"
              >
                {{ domain.domain }}
              </input-radio>
            </cv-radio-group>
          </dd>
        </dl>
        <!-- Keywords -->
        <match-indicator v-if="matchKeyword" />
        <dl class="field-set keywords">
          <dt class="field-label">{{ t("website_keyword_tags") }}</dt>
          <dd v-if="showKeywords" class="field-value">
            <keywords :tags="website.keywords" />
          </dd>
          <dd v-else class="output-text">
            {{ emptyKeywordLabel }}
          </dd>
        </dl>
      </cv-form>
    </template>

    <!-- Choose button -->
    <template #action-button>
      <cv-button class="choose-selected-btn" :disabled="isDisabled" @click="handleChoose">
        <svg-icon name="Checkmark24" kind="action" />
        <span>{{ t("embed_website_choose_button_label") }}</span>
      </cv-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import WhitelistWebsiteApi from "@apis/whitelist.website.api";
import { useErrorHandler } from "@plugins/common/composables/error";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage";
import { loadLocaleMessages } from "@plugins/i18n";
import Keywords from "@plugins/keywords/components/Keywords.vue";
import { isEmpty, isNull } from "@utils/var.check";

interface Domain {
  uuid: string;
  domain: string;
  embeddedType: string;
}

interface Website {
  iconUrl?: string;
  description: string;
  keywords: string[];
  domains: Domain[];
}

interface Props {
  params: ContentSubpageParams & {
    title: string;
    websiteUuid: string;
    highlights?: {
      description?: boolean;
      keyword?: boolean;
      name?: boolean;
    };
  };
}

const props = defineProps<Props>();

const emit = defineEmits<{
  onWebsiteSelected: [
    data: {
      domainUuid: string;
      domainName: string;
      embeddedType: string;
    },
  ];
}>();

const { t } = useI18n();
const router = useRouter();
const { passiveApiFailHandler } = useErrorHandler();

// Content subpage composable
const { visible, titles, hasParentSubpage, actionHidden, actionBack, defaultExpose } =
  useContentSubpage(props.params);

// Expose for parent container ContentSubpageContainer to work properly.
defineExpose({
  ...defaultExpose,
  params: props.params,
});

// State
const website = ref<Website>({
  description: "",
  keywords: [],
  domains: [],
});
const domains = ref<Domain[]>([]);
const isLoading = ref(true);
const selectedValue = ref<string | null>(null);
const emptyKeywordLabel = ref("-");
const parentSubpageName = ref("");

// Computed
const websiteName = computed(() => props.params.title);

const backLabel = computed(() => `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`);

const isDisabled = computed(() => isNull(selectedValue.value));

const showKeywords = computed(() => website.value.keywords.length > 0);

const matchDescription = computed(() =>
  isEmpty(props.params.highlights) ? null : props.params.highlights?.description,
);

const matchKeyword = computed(() =>
  isEmpty(props.params.highlights) ? null : props.params.highlights?.keyword,
);

const matchWebsiteName = computed(() =>
  isEmpty(props.params.highlights) ? null : props.params.highlights?.name,
);

// Methods
const sortDomains = (domains: Domain[]): Domain[] => {
  return domains.sort((a, b) => {
    const fa = a.domain.toLowerCase();
    const fb = b.domain.toLowerCase();

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  });
};

const handleChangeSelection = (selected: string): void => {
  selectedValue.value = selected;
};

const handleChoose = (): void => {
  const selectedDomain = domains.value.find((domain) => domain.uuid === selectedValue.value);

  if (selectedDomain) {
    emit("onWebsiteSelected", {
      domainUuid: selectedValue.value!,
      domainName: selectedDomain.domain,
      embeddedType: selectedDomain.embeddedType,
    });
  }
  visible.value = false;
};

const getIconUrl = (): string => {
  return `${website.value.iconUrl}?t=${Date.now()}`;
};

// Lifecycle
onMounted(async () => {
  const localeMessages = loadLocaleMessages("whitelist-website");

  const loadWebsite = WhitelistWebsiteApi.fetchWhitelistWebsiteDetails(
    props.params.websiteUuid,
  ).then((response) => {
    if (response.code !== "ok") {
      router.push({ name: "Forbidden" });
      return;
    }
    website.value = response.data;
    domains.value = sortDomains(website.value.domains);
    handleChangeSelection(domains.value[0].uuid); // Select first domain by default
  }, passiveApiFailHandler);

  try {
    await Promise.all([localeMessages, loadWebsite]);
    isLoading.value = false;
  } catch (error) {
    passiveApiFailHandler(error);
  }

  if (hasParentSubpage.value) {
    parentSubpageName.value = titles.value.slice(-1)[0];
  }
  titles.value.push(props.params.title);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/abstracts/mixins";

.embed-website-selection-content-subpage {
  .domains {
    .bx--radio-button-group {
      width: 100%;
    }
    .input-radio {
      width: 100%;
      padding: $spacing-04 $spacing-05;
      background-color: $ui-background;
    }
  }

  .image-placeholder {
    display: flex;
    justify-content: center;
    @include aspect-ratio(1);

    img {
      height: 100%;
    }
  }
}
</style>
